import { AlertTriangle, CheckCircle2, Info, Loader2, XCircle } from "lucide-react";
import { ISSUE_TEXT } from "@/lib/audioAnalysis";

const ICON = { bad: XCircle, warn: AlertTriangle, info: Info };
const TONE = { bad: "text-rose-200", warn: "text-amber-200", info: "text-sky-200" };

/** After each take: clipping, background noise, low volume, silence, timing — suggestions only, the take is untouched. */
export default function TakeCheckCard({ result, saving, onRetrySave, onDismiss }) {
  if (!result) return null;
  const { check } = result;
  const issues = check.issues || [];
  const good = !issues.some((c) => ISSUE_TEXT[c]?.level !== "info");
  return (
    <div data-testid="take-check" data-ok={good ? "1" : "0"} className={`rounded-3xl border p-4 ${good ? "border-emerald-300/30 bg-emerald-500/[0.07]" : "border-amber-300/30 bg-amber-500/[0.06]"}`}>
      <div className="flex items-start justify-between gap-3">
        <p className="flex items-center gap-2 font-display text-[15px] font-semibold">
          {good ? <CheckCircle2 className="h-4 w-4 text-emerald-300" /> : <AlertTriangle className="h-4 w-4 text-amber-300" />}
          <span data-testid="take-check-title">{result.takeName ? `${result.trackName} · ${result.takeName}` : "That take didn't record"} — {good ? "sounds clean" : "have a look"}</span>
        </p>
        <button type="button" data-testid="take-check-dismiss" onClick={onDismiss} className="text-[12px] text-white/45 hover:text-white">Hide</button>
      </div>
      {check.peak_db != null ? (
        <p data-testid="take-check-stats" className="mt-1 font-mono text-[11px] text-white/50">peak {check.peak_db} dB · voice {check.rms_db} dB · room {check.noise_db} dB{check.clipped ? ` · ${check.clipped} clipped spots` : ""}</p>
      ) : null}
      <ul className="mt-3 space-y-2">
        {issues.map((code) => {
          const t = ISSUE_TEXT[code];
          if (!t) return null;
          const Icon = ICON[t.level];
          return (
            <li key={code} data-testid={`take-issue-${code}`} className="flex gap-2 text-[13px]">
              <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${TONE[t.level]}`} /><span><b className={TONE[t.level]}>{t.title}.</b> <span className="text-white/70">{t.tip}</span></span>
            </li>
          );
        })}
        {good && !issues.length ? <li className="text-[13px] text-white/70">No clipping, good level, quiet room.</li> : null}
      </ul>
      {result.takeName ? (
        <p data-testid="take-check-save" className="mt-3 font-mono text-[10px] uppercase tracking-wider text-white/45">
          {saving ? <span className="inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Saving to your recordings…</span>
            : result.saved ? "Saved to your recordings and this session · original kept unchanged"
              : result.saveError ? <button type="button" data-testid="take-check-retry" onClick={onRetrySave} className="text-amber-200 underline">Not saved — save take again</button> : null}
        </p>
      ) : null}
    </div>
  );
}
